import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { ISupportTicket } from '../../models/Support/ISupportTicket';
import {
	ThreadStatus,
	ThreadType,
	TicketPriority,
	TicketSource,
	TicketStatus,
} from '../../models/Support/SupportEnums';
import { invalidateTicket, invalidateTicketLists, SUPPORT_PATHS, SUPPORT_QUERY_KEYS } from './supportQueryHelpers';

export function useUpdateTicketStatusMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (status: TicketStatus) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.tickets.status(ticketId), 'PUT', status),
		onSuccess: () => {
			invalidateTicket(queryClient, ticketId);
			invalidateTicketLists(queryClient);
		},
	});
}

export function useAssignTicketMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (assigneeId: string) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.tickets.assign(ticketId), 'PUT', {
				assigneeId: assigneeId || null,
			}),
		onSuccess: () => {
			invalidateTicket(queryClient, ticketId);
			invalidateTicketLists(queryClient);
		},
	});
}

export function useUpdateTicketPriorityMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (priority: TicketPriority) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.tickets.priority(ticketId), 'PUT', priority),
		onSuccess: () => {
			invalidateTicket(queryClient, ticketId);
			invalidateTicketLists(queryClient);
		},
	});
}

export function useUpdateTicketCategoryMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (categoryId: string | null) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.tickets.category(ticketId), 'PUT', {
				categoryId,
			}),
		onSuccess: () => {
			invalidateTicket(queryClient, ticketId);
			invalidateTicketLists(queryClient);
		},
	});
}

export type BulkTicketUpdate =
	| { field: 'status'; status: TicketStatus }
	| { field: 'priority'; priority: TicketPriority }
	| { field: 'assignee'; assigneeId: string | null }
	| { field: 'category'; categoryId: string | null };

interface BulkUpdateTicketsPayload {
	ticketIds: string[];
	update: BulkTicketUpdate;
}

// No batch endpoint exists, so cap concurrent per-ticket updates.
const BULK_UPDATE_CONCURRENCY = 5;

// Attempt every ticket so an isolated failure does not block unrelated updates.
export function useBulkUpdateTicketsMutation() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	const updateOne = (ticketId: string, update: BulkTicketUpdate) => {
		const helper = userSession.webServiceHelper;
		switch (update.field) {
			case 'status':
				return helper.callAsync(SUPPORT_PATHS.tickets.status(ticketId), 'PUT', update.status);
			case 'priority':
				return helper.callAsync(SUPPORT_PATHS.tickets.priority(ticketId), 'PUT', update.priority);
			case 'assignee':
				return helper.callAsync(SUPPORT_PATHS.tickets.assign(ticketId), 'PUT', { assigneeId: update.assigneeId });
			case 'category':
				return helper.callAsync(SUPPORT_PATHS.tickets.category(ticketId), 'PUT', { categoryId: update.categoryId });
			default:
				// Guards unchecked call-boundary casts if the union grows.
				throw new Error('Unknown bulk ticket update field.');
		}
	};

	return useMutation({
		mutationFn: async ({ ticketIds, update }: BulkUpdateTicketsPayload) => {
			let failureCount = 0;
			for (let index = 0; index < ticketIds.length; index += BULK_UPDATE_CONCURRENCY) {
				const chunk = ticketIds.slice(index, index + BULK_UPDATE_CONCURRENCY);
				const results = await Promise.allSettled(chunk.map(ticketId => updateOne(ticketId, update)));
				failureCount += results.filter(result => result.status === 'rejected').length;
			}
			if (failureCount > 0) {
				throw new Error(`Failed to update ${failureCount} of ${ticketIds.length} tickets.`);
			}
		},
		// Partial failures can still mutate tickets, so always refresh caches.
		onSettled: (_data, _error, { ticketIds }) => {
			for (const ticketId of ticketIds) {
				invalidateTicket(queryClient, ticketId);
			}
			invalidateTicketLists(queryClient);
		},
	});
}

export interface CreateTicketContact {
	/** Known-user link; the server resolves email, name, and account from the user. */
	userId?: string;
	email?: string;
	name?: string;
}

export interface CreateTicketPayload {
	subject: string;
	/** Editor HTML for the first thread's body; the API sanitizes every thread body on write. */
	descriptionHtml: string;
	mailboxId: string;
	priority: TicketPriority;
	categoryId?: string;
	accountId?: string;
	contact?: CreateTicketContact;
	/** Already-uploaded attachment ids (`POST email/attachment`); attached to the description thread. */
	attachmentIds?: string[];
}

export interface CreateTicketResult {
	ticket: ISupportTicket;
	/** Human-readable fragments for follow-up steps that failed after the ticket itself was created. */
	warnings: string[];
}

/**
 * Creates a ticket from the new-ticket form. `POST support/tickets` only binds
 * subject/priority/mailbox/category/manual-contact, so the contact link, account link, and the
 * description (the ticket's first thread) are follow-up calls. Once the ticket exists, a failed
 * follow-up is reported as a warning rather than failing the whole submission — the agent can
 * finish the linking from the ticket view.
 */
export function useCreateTicketMutation() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (payload: CreateTicketPayload): Promise<CreateTicketResult> => {
			const helper = userSession.webServiceHelper;
			const { subject, descriptionHtml, mailboxId, priority, categoryId, accountId, contact, attachmentIds } = payload;
			const isKnownContact = !!contact?.userId;

			const ticket = await helper.callAsync<ISupportTicket>(SUPPORT_PATHS.tickets.create, 'POST', {
				subject,
				priority,
				mailboxId,
				categoryId: categoryId || undefined,
				source: TicketSource.Manual,
				// A known contact is linked via the customer endpoint below; only manual contacts bind here.
				contactEmail: isKnownContact ? undefined : contact?.email || undefined,
				contactName: isKnownContact ? undefined : contact?.name || undefined,
			});

			const warnings: string[] = [];
			const tryStep = async (failureLabel: string, step: () => Promise<unknown>) => {
				try {
					await step();
					return true;
				} catch {
					warnings.push(failureLabel);
					return false;
				}
			};

			if (isKnownContact) {
				const linked = await tryStep('link the contact', () =>
					helper.callAsync(SUPPORT_PATHS.tickets.customer(ticket.id), 'PUT', { userId: contact.userId })
				);
				// The customer link carries the account; fall back to a direct account link if it failed.
				if (!linked && accountId) {
					await tryStep('link the account', () =>
						helper.callAsync(SUPPORT_PATHS.tickets.account(ticket.id), 'PUT', { accountId })
					);
				}
			} else if (accountId) {
				await tryStep('link the account', () =>
					helper.callAsync(SUPPORT_PATHS.tickets.account(ticket.id), 'PUT', { accountId })
				);
			}

			const body = descriptionHtml.trim();
			const threadAttachmentIds = attachmentIds?.length ? attachmentIds : undefined;
			if (body || threadAttachmentIds) {
				// With a contact the description is the customer's request (authored by them, like an
				// inbound email); without one it is an internal write-up, so it lands as a note.
				const thread = contact?.email
					? {
							type: ThreadType.CustomerMessage,
							status: ThreadStatus.Received,
							body,
							authorEmail: contact.email,
							authorName: contact.name || undefined,
							attachmentIds: threadAttachmentIds,
						}
					: { type: ThreadType.Note, body, attachmentIds: threadAttachmentIds };
				await tryStep('add the description', () =>
					helper.callAsync(SUPPORT_PATHS.threads.list(ticket.id), 'POST', thread)
				);
			}

			return { ticket, warnings };
		},
		onSuccess: ({ ticket }) => {
			invalidateTicket(queryClient, ticket.id);
			invalidateTicketLists(queryClient);
		},
	});
}

interface ChangeCustomerPayload {
	userId?: string;
	contactEmail?: string;
	contactName?: string;
}

/** Change the customer/contact attached to a support ticket. */
export function useChangeCustomerMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: ChangeCustomerPayload) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.tickets.customer(ticketId), 'PUT', payload),
		onSuccess: () => {
			invalidateTicket(queryClient, ticketId);
			invalidateTicketLists(queryClient);
		},
	});
}

/** Merge a source ticket into the target ticket. All threads move to the target ticket. */
export function useMergeTicketMutation(targetTicketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (sourceTicketId: string) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.tickets.merge(targetTicketId, sourceTicketId), 'POST'),
		onSuccess: (_data, sourceTicketId) => {
			invalidateTicket(queryClient, targetTicketId);
			invalidateTicket(queryClient, sourceTicketId);
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.tickets.threads(targetTicketId) });
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.tickets.threads(sourceTicketId) });
			invalidateTicketLists(queryClient);
		},
	});
}

// --- GitHub issue mutations ---

/** Generate AI markdown for a GitHub issue from a ticket. */
export function useGenerateGitHubIssueMarkdownMutation(ticketId: string) {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: (description: string) =>
			userSession.webServiceHelper.callAsync<{ title: string; body: string }>(
				SUPPORT_PATHS.tickets.githubIssueMarkdown(ticketId),
				'POST',
				{ description }
			),
	});
}
